import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Pencil, Plus, RotateCcw, Tags } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { BrandSummaryType } from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { TabHeader } from '../components/config-tabs.tsx'
import {
  createBrand,
  fetchBrands,
  renameBrand,
  setBrandStatus,
} from '../lib/api/brands.ts'
import { ApiError } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * O cadastro de marcas — F158.
 *
 * ## Por que esta tela existe, em números
 *
 * `product.brand` era texto livre: **2.967 de 103.503 produtos têm marca —
 * 2,9%** —, e a pergunta que o dono fez ("quais marcas mais têm troca")
 * respondia uma linha, `(sem marca) | 827`. O campo livre não é liberdade: é
 * adivinhação, e "Multilaser", "multilaser" e "MULTILASER" são três linhas de
 * qualquer relatório.
 *
 * Esta tela é o vocabulário. O formulário de produto passa a ESCOLHER dele, e
 * o servidor recusa o que não estiver aqui.
 *
 * ## A lista inteira, sem busca e sem paginação
 *
 * Uma loja tem dezenas de marcas. A seção 27 da Master Specification fala de
 * listas que crescem para cem mil linhas, e esta não é uma delas — é o mesmo
 * raciocínio de Marcadores (F088).
 *
 * ## `usageCount` não é enfeite
 *
 * É o que faz arquivar ser uma decisão informada, e é também o mapa do trabalho
 * que falta: a marca com 723 produtos e a marca com nenhum pedem coisas
 * diferentes de quem está olhando.
 *
 * ## Editar (F160)
 *
 * O gerente pediu "editar marca". O servidor renomeia E leva o nome novo aos
 * produtos, às regras de follow-up e às campanhas não enviadas, numa
 * transação — senão os produtos ficariam com um nome que saiu do cadastro. Se o
 * nome novo já é outra marca ativa, as duas viram uma, e a tela diz isso: é
 * uma coisa diferente de renomear, e quem clicou precisa saber qual aconteceu.
 */

const BrandsPanel = (): ReactNode => {
  const queryClient = useQueryClient()
  const { allows } = useAuth()
  const canManage = allows('catalog.write')

  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['brands', 'all'],
    queryFn: async ({ signal }) => await fetchBrands(undefined, signal),
  })

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['brands'] })
  }

  const create = useMutation({
    mutationFn: async () => await createBrand({ name }),
    onSuccess: async () => {
      setName('')
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível criar a marca.'
      )
    },
  })

  const changeStatus = useMutation({
    mutationFn: async (brand: BrandSummaryType) =>
      await setBrandStatus(brand.id, {
        status: brand.status === 'active' ? 'archived' : 'active',
        version: brand.version,
      }),
    onSuccess: async () => {
      setError(null)
      await invalidate()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível alterar a marca.'
      )
    },
  })

  /** A marca aberta para editar, e o nome que está sendo digitado. */
  const [editing, setEditing] = useState<BrandSummaryType | null>(null)
  const [newName, setNewName] = useState('')
  /** O que acabou de acontecer: "Maxter pro virou Maxter Pro — 271 produtos". */
  const [notice, setNotice] = useState<string | null>(null)

  const rename = useMutation({
    mutationFn: async (brand: BrandSummaryType) =>
      await renameBrand(brand.id, { name: newName, version: brand.version }),
    onSuccess: async (result) => {
      setError(null)
      setEditing(null)
      const produtos = `${String(result.productsMoved)} produto${result.productsMoved === 1 ? '' : 's'}`
      setNotice(
        result.merged
          ? `"${result.previousName}" foi juntada a "${result.brand.name}" — ${produtos} passaram para ela.`
          : `"${result.previousName}" agora é "${result.brand.name}" — ${produtos} acompanharam.`
      )
      await invalidate()
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível renomear a marca.'
      )
    },
  })

  const brands = query.data ?? []
  const semMarca = brands.filter((brand) => brand.usageCount === 0).length

  return (
    <div className="flex flex-col gap-4">
      {canManage ? (
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim() === '') return
            create.mutate()
          }}
        >
          <label className="flex min-w-48 flex-1 flex-col gap-1">
            <span className="text-(length:--text-xs) text-(--text-muted)">
              Nome
            </span>
            <Input
              value={name}
              maxLength={120}
              placeholder="Multilaser"
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          </label>
          <Button
            type="submit"
            variant="primary"
            disabled={name.trim() === '' || create.isPending}
            leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
          >
            Criar
          </Button>
        </form>
      ) : null}

      {error === null ? null : (
        <p role="alert" className="text-(length:--text-sm) text-(--danger)">
          {error}
        </p>
      )}

      {notice === null ? null : (
        <InlineAlert tone="success">{notice}</InlineAlert>
      )}

      {query.isPending ? <Spinner label="Carregando marcas" /> : null}

      {/*
       * O ramo de erro, e não só vazio (F069): sem ele, uma consulta que falha
       * faz a tela AFIRMAR que a loja não tem marca nenhuma enquanto o servidor
       * está fora do ar — e quem lê "nenhuma ainda" cadastra tudo de novo.
       */}
      {query.isError ? (
        <EmptyState
          icon={<Tags aria-hidden="true" className="h-6 w-6" />}
          title="Não foi possível carregar as marcas"
          description="Verifique a conexão e tente de novo."
          action={
            <Button
              variant="secondary"
              onClick={() => {
                void query.refetch()
              }}
            >
              Tentar de novo
            </Button>
          }
        />
      ) : null}

      {!query.isPending && !query.isError && brands.length === 0 ? (
        <EmptyState
          icon={<Tags aria-hidden="true" className="h-6 w-6" />}
          title="Nenhuma marca ainda"
          description={
            canManage
              ? 'Crie a primeira acima. Elas aparecem no cadastro de produto e no filtro da lista.'
              : 'Quem administra o catálogo ainda não cadastrou nenhuma.'
          }
        />
      ) : null}

      {/*
       * A marca sem nenhum produto não é erro — pode ter acabado de ser criada.
       * Mas uma tela cheia delas quer dizer que o catálogo não foi marcado, que
       * é exatamente o trabalho que falta para o relatório de marca valer.
       */}
      {semMarca > 0 && semMarca === brands.length && brands.length > 0 ? (
        <InlineAlert tone="info" title="Nenhum produto tem marca ainda">
          As marcas existem, mas nenhuma está em produto nenhum. O relatório de
          marca só começa a responder quando os produtos apontarem para elas.
        </InlineAlert>
      ) : null}

      {brands.length === 0 || query.isError ? null : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Marca</TableHeaderCell>
                <TableHeaderCell>Em uso</TableHeaderCell>
                <TableHeaderCell>Situação</TableHeaderCell>
                {canManage ? (
                  <TableHeaderCell className="text-right">
                    Ações
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {brands.map((brand) => (
                <TableRow key={brand.id}>
                  <TableCell>{brand.name}</TableCell>
                  <TableCell>
                    {brand.usageCount === 0
                      ? '—'
                      : `${String(brand.usageCount)} produto${brand.usageCount === 1 ? '' : 's'}`}
                  </TableCell>
                  <TableCell>
                    {brand.status === 'active' ? (
                      'Ativa'
                    ) : (
                      <span className="text-(--text-muted)">Arquivada</span>
                    )}
                  </TableCell>
                  {canManage ? (
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        leadingIcon={
                          <Pencil aria-hidden="true" className="h-4 w-4" />
                        }
                        onClick={() => {
                          setNewName(brand.name)
                          setEditing(brand)
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={changeStatus.isPending}
                        onClick={() => {
                          changeStatus.mutate(brand)
                        }}
                        leadingIcon={
                          brand.status === 'active' ? (
                            <Archive aria-hidden="true" className="h-4 w-4" />
                          ) : (
                            <RotateCcw aria-hidden="true" className="h-4 w-4" />
                          )
                        }
                      >
                        {brand.status === 'active' ? 'Arquivar' : 'Reativar'}
                      </Button>
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing === null ? null : (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) setEditing(null)
          }}
        >
          <DialogContent
            title={`Editar a marca "${editing.name}"`}
            description={
              editing.usageCount === 0
                ? 'Nenhum produto usa esta marca ainda.'
                : editing.usageCount === 1
                  ? 'O produto com esta marca passa a ter o nome novo. Se o nome novo já for outra marca, as duas viram uma.'
                  : `Os ${editing.usageCount.toLocaleString('pt-BR')} produtos com esta marca passam a ter o nome novo. Se o nome novo já for outra marca, as duas viram uma.`
            }
          >
            <form
              id="editar-marca"
              onSubmit={(event) => {
                event.preventDefault()
                if (newName.trim() === '') return
                rename.mutate(editing)
              }}
            >
              <Field label="Nome">
                {(field) => (
                  <Input
                    {...field}
                    value={newName}
                    maxLength={120}
                    onChange={(event) => {
                      setNewName(event.target.value)
                    }}
                  />
                )}
              </Field>
            </form>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => {
                  setEditing(null)
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="editar-marca"
                variant="primary"
                disabled={rename.isPending || newName.trim() === ''}
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export const MarcasPage = (): ReactNode => (
  <>
    <TabHeader
      title="Marcas"
      description={
        <>
          O vocabulário de marcas do catálogo. O cadastro de produto{' '}
          <strong>escolhe daqui</strong> em vez de digitar, e é isso que faz
          &ldquo;quais marcas mais vendem&rdquo; ter resposta. Arquivar tira a
          marca do seletor <strong>sem mexer</strong> nos produtos que já a têm.
        </>
      }
    />

    <BrandsPanel />
  </>
)
